import { useEffect, useMemo, useState } from 'react'
import AdminLayout from '../../components/AdminLayout'
import { supabase } from '../../lib/supabase'

const fmt = (n) => Number(n || 0).toLocaleString('es-UY', { minimumFractionDigits: 2, maximumFractionDigits: 2 })

export default function Sueldos() {
  const [tab, setTab] = useState('liquidaciones')
  const [empleados, setEmpleados] = useState([])
  const [liquidaciones, setLiquidaciones] = useState([])
  const [filtroMes, setFiltroMes] = useState(() => new Date().toISOString().slice(0, 7))
  const [loading, setLoading] = useState(true)
  const [modalEmp, setModalEmp] = useState(null)
  const [generando, setGenerando] = useState(false)

  useEffect(() => { cargar() }, [filtroMes])

  async function cargar() {
    setLoading(true)
    const desde = `${filtroMes}-01`
    const [{ data: emps }, { data: liqs }] = await Promise.all([
      supabase.from('empleados').select('*').order('activo', { ascending: false }).order('nombre'),
      supabase.from('liquidaciones_sueldos').select('*, empleado:empleados(nombre, documento, cargo)').gte('mes', desde).lte('mes', desde).order('created_at'),
    ])
    setEmpleados(emps || [])
    setLiquidaciones(liqs || [])
    setLoading(false)
  }

  async function generarMes() {
    if (!confirm(`¿Generar liquidaciones para ${filtroMes}? Se calcularán automáticamente para los empleados activos.`)) return
    setGenerando(true)
    try {
      const { data, error } = await supabase.rpc('generar_liquidaciones_mes', { p_mes: `${filtroMes}-01` })
      if (error) throw error
      alert(`✓ ${data.liquidaciones_creadas} liquidaciones creadas en borrador.\n\nAhora revisalas y aprobalas una por una.`)
      cargar()
    } catch (e) { alert('Error: ' + e.message) }
    finally { setGenerando(false) }
  }

  async function aprobar(liq) {
    if (!confirm(`¿Aprobar liquidación de ${liq.empleado.nombre} por $${fmt(liq.liquido)}?\n\nSe genera asiento contable con devengamiento + aportes.`)) return
    try {
      const { error } = await supabase.rpc('aprobar_liquidacion_sueldo', { p_liquidacion_id: liq.id })
      if (error) throw error
      cargar()
    } catch (e) { alert('Error: ' + e.message) }
  }

  async function borrar(liq) {
    if (liq.estado !== 'borrador') { alert('Solo se pueden borrar liquidaciones en borrador'); return }
    if (!confirm('¿Borrar esta liquidación?')) return
    await supabase.from('liquidaciones_sueldos').delete().eq('id', liq.id)
    cargar()
  }

  async function guardarEmpleado(form) {
    try {
      const payload = {
        nombre: form.nombre,
        documento: form.documento,
        cargo: form.cargo,
        fecha_ingreso: form.fecha_ingreso,
        fecha_egreso: form.fecha_egreso || null,
        sueldo_bruto_mensual: Number(form.sueldo_bruto_mensual),
        bps_personal_pct: Number(form.bps_personal_pct),
        frl_personal_pct: Number(form.frl_personal_pct),
        bps_patronal_pct: Number(form.bps_patronal_pct),
        frl_patronal_pct: Number(form.frl_patronal_pct),
        irpf_pct_estimado: Number(form.irpf_pct_estimado),
        activo: !!form.activo,
        notas: form.notas,
      }
      if (form.id) await supabase.from('empleados').update(payload).eq('id', form.id)
      else await supabase.from('empleados').insert(payload)
      setModalEmp(null)
      cargar()
    } catch (e) { alert('Error: ' + e.message) }
  }

  const totales = useMemo(() => {
    const t = { bruto: 0, liquido: 0, bps: 0, costo: 0, cant: liquidaciones.length }
    for (const l of liquidaciones) {
      t.bruto += Number(l.sueldo_bruto)
      t.liquido += Number(l.liquido)
      t.bps += Number(l.bps_personal) + Number(l.bps_patronal)
      t.costo += Number(l.costo_total_limpack)
    }
    return t
  }, [liquidaciones])

  return (
    <AdminLayout>
      <div className="su-wrap">
        <div className="su-headline">
          <div>
            <h1>Sueldos y personal</h1>
            <p>Empleados, liquidaciones mensuales con aportes uruguayos (BPS, FRL, IRPF) y asientos contables automáticos.</p>
          </div>
        </div>

        <div className="su-tabs">
          <button className={tab === 'liquidaciones' ? 'active' : ''} onClick={() => setTab('liquidaciones')}>📋 Liquidaciones</button>
          <button className={tab === 'empleados' ? 'active' : ''} onClick={() => setTab('empleados')}>👥 Empleados ({empleados.length})</button>
        </div>

        {tab === 'liquidaciones' && (
          <>
            <div className="su-toolbar">
              <label className="su-fil">
                <span>Mes</span>
                <input type="month" value={filtroMes} onChange={e => setFiltroMes(e.target.value)} />
              </label>
              <button className="su-btn-primary" onClick={generarMes} disabled={generando || empleados.filter(e => e.activo).length === 0}>
                {generando ? 'Generando…' : '⚙️ Generar liquidaciones del mes'}
              </button>
            </div>

            <div className="su-kpis">
              <KpiCard label="Liquidaciones" value={totales.cant} color="#1A3461" />
              <KpiCard label="Total bruto" value={'$' + fmt(totales.bruto)} color="#0ea5e9" sub="suma sueldos brutos" />
              <KpiCard label="Total líquido" value={'$' + fmt(totales.liquido)} color="#16a34a" sub="lo que cobran los empleados" />
              <KpiCard label="Total BPS (pers + pat)" value={'$' + fmt(totales.bps)} color="#92400e" sub="a depositar en BPS" />
              <KpiCard label="Costo total Limpack" value={'$' + fmt(totales.costo)} color="#dc2626" sub="bruto + aportes patronales" />
            </div>

            {loading ? <div className="su-loading">Cargando…</div> :
             liquidaciones.length === 0 ? (
              <div className="su-empty">
                <strong>Sin liquidaciones este mes</strong>
                <p>{empleados.filter(e => e.activo).length === 0
                  ? 'Cargá empleados primero desde la pestaña Empleados.'
                  : 'Tocá "Generar liquidaciones del mes" para calcularlas automáticamente.'}</p>
              </div>
            ) : (
              <div className="su-tabla-wrap">
                <table className="su-tabla">
                  <thead>
                    <tr>
                      <th>Empleado</th>
                      <th style={{ textAlign: 'right' }}>Bruto</th>
                      <th style={{ textAlign: 'right' }}>BPS pers.</th>
                      <th style={{ textAlign: 'right' }}>FRL</th>
                      <th style={{ textAlign: 'right' }}>IRPF</th>
                      <th style={{ textAlign: 'right' }}>Líquido</th>
                      <th style={{ textAlign: 'right' }}>BPS pat.</th>
                      <th style={{ textAlign: 'right' }}>Costo Limpack</th>
                      <th>Estado</th>
                      <th></th>
                    </tr>
                  </thead>
                  <tbody>
                    {liquidaciones.map(l => (
                      <tr key={l.id}>
                        <td>
                          <div className="su-emp-nom">{l.empleado.nombre}</div>
                          {l.empleado.cargo && <div className="su-emp-cargo">{l.empleado.cargo}</div>}
                        </td>
                        <td style={{ textAlign: 'right' }}>${fmt(l.sueldo_bruto)}</td>
                        <td style={{ textAlign: 'right', color: '#dc2626' }}>−${fmt(l.bps_personal)}</td>
                        <td style={{ textAlign: 'right', color: '#dc2626' }}>−${fmt(l.frl_personal)}</td>
                        <td style={{ textAlign: 'right', color: '#dc2626' }}>−${fmt(l.irpf)}</td>
                        <td style={{ textAlign: 'right', fontWeight: 800, color: '#16a34a' }}>${fmt(l.liquido)}</td>
                        <td style={{ textAlign: 'right', color: '#92400e' }}>${fmt(l.bps_patronal)}</td>
                        <td style={{ textAlign: 'right', fontWeight: 700 }}>${fmt(l.costo_total_limpack)}</td>
                        <td>
                          <span className={`su-pill su-pill-${l.estado}`}>{l.estado}</span>
                        </td>
                        <td style={{ whiteSpace: 'nowrap' }}>
                          {l.estado === 'borrador' && <>
                            <button className="su-btn-approve" onClick={() => aprobar(l)}>✓ Aprobar</button>
                            <button className="su-btn-del" onClick={() => borrar(l)}>×</button>
                          </>}
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            )}

            <div className="su-info">
              <strong>📚 Cómo se calcula la liquidación</strong>
              <ul>
                <li><b>Bruto</b> = sueldo configurado en empleado</li>
                <li><b>BPS personal</b> = bruto × % BPS personal (default 15%, ajustable por empleado)</li>
                <li><b>FRL personal</b> = bruto × 0,125%</li>
                <li><b>IRPF</b> = bruto × % estimado (Roberto ajusta según tabla DGI)</li>
                <li><b>Líquido</b> = bruto − BPS − FRL − IRPF (lo que cobra el empleado)</li>
                <li><b>BPS patronal</b> = bruto × 12,625% (incluye jubilación 7,5 + salud 5 + FRL 0,125)</li>
                <li><b>Costo Limpack</b> = bruto + BPS patronal + FRL patronal</li>
              </ul>
              <p>Al aprobar la liquidación se genera el asiento contable: DEBE Sueldos + BPS pat + FRL pat / HABER Líquido a pagar + BPS total + FRL total + IRPF.</p>
            </div>
          </>
        )}

        {tab === 'empleados' && (
          <>
            <div className="su-toolbar">
              <button className="su-btn-primary" onClick={() => setModalEmp({
                nombre: '', documento: '', cargo: '', fecha_ingreso: new Date().toISOString().slice(0, 10),
                fecha_egreso: '', sueldo_bruto_mensual: 0,
                bps_personal_pct: 15.0, frl_personal_pct: 0.125,
                bps_patronal_pct: 12.625, frl_patronal_pct: 0.125,
                irpf_pct_estimado: 0, activo: true, notas: '',
              })}>+ Nuevo empleado</button>
            </div>

            {empleados.length === 0 ? (
              <div className="su-empty">
                <strong>Sin empleados cargados</strong>
                <p>Cargá vos, Gimena, Roberto (si cobra), repartidores. Después podés generar liquidaciones mensuales.</p>
              </div>
            ) : (
              <div className="su-tabla-wrap">
                <table className="su-tabla">
                  <thead>
                    <tr>
                      <th>Nombre</th>
                      <th>Documento</th>
                      <th>Cargo</th>
                      <th>Ingreso</th>
                      <th style={{ textAlign: 'right' }}>Sueldo bruto</th>
                      <th>BPS pers / pat</th>
                      <th>Estado</th>
                      <th></th>
                    </tr>
                  </thead>
                  <tbody>
                    {empleados.map(e => (
                      <tr key={e.id} className={!e.activo ? 'su-row-inactivo' : ''}>
                        <td><b>{e.nombre}</b></td>
                        <td style={{ fontSize: 11, color: '#64748b' }}>{e.documento || '—'}</td>
                        <td>{e.cargo || '—'}</td>
                        <td>{e.fecha_ingreso}</td>
                        <td style={{ textAlign: 'right', fontWeight: 700 }}>${fmt(e.sueldo_bruto_mensual)}</td>
                        <td style={{ fontSize: 11, color: '#64748b' }}>{e.bps_personal_pct}% / {e.bps_patronal_pct}%</td>
                        <td>{e.activo ? <span className="su-pill su-pill-aprobada">Activo</span> : <span className="su-pill su-pill-borrador">Inactivo</span>}</td>
                        <td><button className="su-btn-edit" onClick={() => setModalEmp(e)}>Editar</button></td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            )}
          </>
        )}
      </div>

      {modalEmp && <ModalEmpleado initial={modalEmp} onClose={() => setModalEmp(null)} onSave={guardarEmpleado} />}

      <style>{CSS}</style>
    </AdminLayout>
  )
}

function ModalEmpleado({ initial, onClose, onSave }) {
  const [f, setF] = useState(initial)
  const upd = (k) => (e) => setF({ ...f, [k]: e.target.value })
  const updBool = (k) => (e) => setF({ ...f, [k]: e.target.checked })

  function submit(e) {
    e.preventDefault()
    if (!f.nombre.trim()) { alert('Nombre obligatorio'); return }
    if (!f.fecha_ingreso) { alert('Fecha ingreso obligatoria'); return }
    onSave(f)
  }

  // Cálculo en vivo de líquido estimado
  const bruto = Number(f.sueldo_bruto_mensual || 0)
  const liquido = bruto - bruto * (Number(f.bps_personal_pct) / 100) - bruto * (Number(f.frl_personal_pct) / 100) - bruto * (Number(f.irpf_pct_estimado) / 100)
  const costo = bruto + bruto * (Number(f.bps_patronal_pct) / 100) + bruto * (Number(f.frl_patronal_pct) / 100)

  return (
    <div className="su-modal-bg" onClick={onClose}>
      <div className="su-modal" onClick={e => e.stopPropagation()}>
        <button className="su-modal-close" onClick={onClose}>×</button>
        <h2>{f.id ? 'Editar empleado' : 'Nuevo empleado'}</h2>
        <form onSubmit={submit}>
          <div className="su-form-row">
            <Field label="Nombre *"><input type="text" value={f.nombre} onChange={upd('nombre')} required /></Field>
            <Field label="Documento (CI)">
              <input type="text" value={f.documento || ''} onChange={upd('documento')} placeholder="Ej: 36955508" inputMode="numeric" />
              <small style={{ display: 'block', fontSize: 11, color: '#64748B', marginTop: 3 }}>7 u 8 dígitos, con o sin puntos y guión</small>
            </Field>
          </div>
          <div className="su-form-row">
            <Field label="Cargo"><input type="text" value={f.cargo || ''} onChange={upd('cargo')} placeholder="Repartidor, Admin, etc" /></Field>
            <Field label="Fecha ingreso *"><input type="date" value={f.fecha_ingreso} onChange={upd('fecha_ingreso')} required /></Field>
          </div>
          <Field label="Fecha egreso (si dejó de trabajar)">
            <input type="date" value={f.fecha_egreso || ''} onChange={upd('fecha_egreso')} />
          </Field>

          <h3 className="su-section">💰 Sueldo y aportes</h3>
          <Field label="Sueldo bruto mensual *">
            <input type="number" step="0.01" value={f.sueldo_bruto_mensual} onChange={upd('sueldo_bruto_mensual')} required />
          </Field>

          <div className="su-form-row su-form-row-3">
            <Field label="BPS personal %"><input type="number" step="0.001" value={f.bps_personal_pct} onChange={upd('bps_personal_pct')} /></Field>
            <Field label="FRL personal %"><input type="number" step="0.001" value={f.frl_personal_pct} onChange={upd('frl_personal_pct')} /></Field>
            <Field label="IRPF estimado %"><input type="number" step="0.001" value={f.irpf_pct_estimado} onChange={upd('irpf_pct_estimado')} /></Field>
          </div>
          <div className="su-form-row">
            <Field label="BPS patronal %"><input type="number" step="0.001" value={f.bps_patronal_pct} onChange={upd('bps_patronal_pct')} /></Field>
            <Field label="FRL patronal %"><input type="number" step="0.001" value={f.frl_patronal_pct} onChange={upd('frl_patronal_pct')} /></Field>
          </div>

          <div className="su-calc">
            <div><span>Bruto:</span> <b>${fmt(bruto)}</b></div>
            <div><span>Líquido (lo que cobra):</span> <b style={{ color: '#16a34a' }}>${fmt(liquido)}</b></div>
            <div><span>Costo total Limpack:</span> <b style={{ color: '#dc2626' }}>${fmt(costo)}</b></div>
          </div>

          <Field label="">
            <label style={{ display: 'flex', alignItems: 'center', gap: 8, cursor: 'pointer' }}>
              <input type="checkbox" checked={!!f.activo} onChange={updBool('activo')} /> Empleado activo
            </label>
          </Field>
          <Field label="Notas"><textarea rows={2} value={f.notas || ''} onChange={upd('notas')} /></Field>

          <div className="su-modal-actions">
            <button type="button" className="su-btn-secondary" onClick={onClose}>Cancelar</button>
            <button type="submit" className="su-btn-primary">Guardar</button>
          </div>
        </form>
      </div>
    </div>
  )
}

function Field({ label, children }) { return <label className="su-field"><span>{label}</span>{children}</label> }
function KpiCard({ label, value, sub, color }) {
  return <div className="su-kpi"><div className="su-kpi-l">{label}</div><div className="su-kpi-v" style={{ color }}>{value}</div>{sub && <div className="su-kpi-s">{sub}</div>}</div>
}

const CSS = `
.su-wrap { padding: 16px 20px 32px; max-width: 1400px; margin: 0 auto }
.su-headline h1 { font-size: 26px; font-weight: 900; color: #1A3461; margin: 0 0 4px; letter-spacing: -.02em }
.su-headline p { font-size: 13px; color: #64748b; margin: 0 0 16px }
.su-tabs { display: flex; gap: 4px; margin-bottom: 12px; border-bottom: 2px solid #e2e8f0 }
.su-tabs button { background: transparent; border: 0; padding: 10px 16px; font-size: 13px; font-weight: 700; color: #64748b; cursor: pointer; border-bottom: 2px solid transparent; margin-bottom: -2px; font-family: inherit }
.su-tabs button.active { color: #1A3461; border-bottom-color: #1A3461 }
.su-toolbar { display: flex; gap: 12px; align-items: flex-end; margin-bottom: 14px; flex-wrap: wrap }
.su-fil { display: flex; flex-direction: column; gap: 3px }
.su-fil span { font-size: 11px; color: #475569; font-weight: 700; text-transform: uppercase; letter-spacing: .04em }
.su-fil input { padding: 7px 10px; border: 1px solid #cbd5e1; border-radius: 7px; font-size: 13px; font-family: inherit }
.su-kpis { display: grid; grid-template-columns: repeat(auto-fit, minmax(170px, 1fr)); gap: 10px; margin-bottom: 14px }
.su-kpi { background: #fff; border: 1px solid #e2e8f0; border-radius: 10px; padding: 12px 14px }
.su-kpi-l { font-size: 11px; color: #94a3b8; text-transform: uppercase; font-weight: 800; letter-spacing: .04em }
.su-kpi-v { font-size: 20px; font-weight: 900 }
.su-kpi-s { font-size: 11px; color: #64748b }
.su-loading, .su-empty { padding: 50px; text-align: center; color: #94a3b8; background: #fff; border: 1px solid #e2e8f0; border-radius: 10px }
.su-empty strong { display: block; color: #1A3461; font-size: 15px; margin-bottom: 6px }
.su-tabla-wrap { background: #fff; border: 1px solid #e2e8f0; border-radius: 10px; overflow: auto }
.su-tabla { width: 100%; border-collapse: collapse; font-size: 12.5px; min-width: 1100px }
.su-tabla th { text-align: left; padding: 10px 12px; background: #f8fafc; font-weight: 700; color: #475569; font-size: 11px; text-transform: uppercase; letter-spacing: .04em; border-bottom: 1px solid #e2e8f0 }
.su-tabla td { padding: 8px 12px; border-bottom: 1px solid #f1f5f9; color: #1A3461 }
.su-row-inactivo { opacity: .5 }
.su-emp-nom { font-weight: 700 }
.su-emp-cargo { font-size: 11px; color: #64748b }
.su-pill { padding: 3px 8px; border-radius: 5px; font-size: 10.5px; font-weight: 800; text-transform: uppercase; letter-spacing: .04em }
.su-pill-borrador { background: #f1f5f9; color: #64748b }
.su-pill-aprobada { background: #d1fae5; color: #065f46 }
.su-pill-pagada { background: #dbeafe; color: #1e40af }
.su-pill-anulada { background: #fee2e2; color: #991b1b }
.su-btn-primary { background: #1A3461; color: #fff; border: 0; padding: 9px 16px; border-radius: 8px; font-size: 13px; font-weight: 800; cursor: pointer; font-family: inherit }
.su-btn-primary:hover { background: #0f2147 }
.su-btn-primary:disabled { opacity: .5; cursor: not-allowed }
.su-btn-secondary { background: #fff; border: 1px solid #cbd5e1; color: #475569; padding: 9px 16px; border-radius: 8px; font-size: 13px; font-weight: 700; cursor: pointer; font-family: inherit }
.su-btn-approve { background: #16a34a; color: #fff; border: 0; padding: 5px 11px; border-radius: 5px; font-size: 11px; font-weight: 800; cursor: pointer; font-family: inherit; margin-right: 4px }
.su-btn-edit { background: transparent; border: 1px solid #cbd5e1; color: #475569; padding: 4px 10px; border-radius: 5px; font-size: 11px; font-weight: 700; cursor: pointer; font-family: inherit }
.su-btn-del { background: transparent; border: 1px solid #fecaca; color: #b91c1c; padding: 4px 9px; border-radius: 5px; font-weight: 800; cursor: pointer; font-family: inherit }
.su-info { margin-top: 22px; padding: 14px 18px; background: #eff6ff; border: 1px solid #bfdbfe; border-radius: 10px; font-size: 12.5px; color: #1e3a8a; line-height: 1.6 }
.su-info strong { font-size: 13px }
.su-info ul { margin: 8px 0; padding-left: 22px }
.su-info b { color: #1e40af }
.su-modal-bg { position: fixed; inset: 0; background: rgba(15,31,61,.55); z-index: 100; display: flex; align-items: center; justify-content: center; padding: 16px }
.su-modal { background: #fff; border-radius: 14px; max-width: 640px; width: 100%; max-height: 92vh; overflow-y: auto; padding: 26px 28px; position: relative }
.su-modal h2 { margin: 0 0 14px; font-size: 20px; color: #1A3461; font-weight: 900 }
.su-section { font-size: 13px; font-weight: 800; color: #475569; margin: 16px 0 8px; padding-top: 12px; border-top: 1px solid #e2e8f0 }
.su-modal-close { position: absolute; top: 12px; right: 12px; background: none; border: 0; font-size: 22px; cursor: pointer; color: #64748b }
.su-modal-actions { display: flex; gap: 10px; justify-content: flex-end; margin-top: 16px; padding-top: 14px; border-top: 1px solid #e2e8f0 }
.su-form-row { display: grid; grid-template-columns: 1fr 1fr; gap: 12px }
.su-form-row-3 { grid-template-columns: 1fr 1fr 1fr }
@media (max-width: 540px) { .su-form-row, .su-form-row-3 { grid-template-columns: 1fr } }
.su-field { display: flex; flex-direction: column; gap: 4px; margin-bottom: 12px }
.su-field > span { font-size: 12px; font-weight: 700; color: #475569 }
.su-field input, .su-field textarea { padding: 9px 11px; border: 1px solid #cbd5e1; border-radius: 8px; font-size: 14px; font-family: inherit; color: #1A3461 }
.su-calc { background: #f8fafc; border: 1px solid #e2e8f0; border-radius: 8px; padding: 10px 14px; margin: 8px 0 12px; font-size: 13px }
.su-calc div { display: flex; justify-content: space-between; padding: 3px 0 }
.su-calc span { color: #64748b }
.su-calc b { color: #1A3461 }
`
